import find from 'lodash/fp/find';
import { computed } from '@nuxtjs/composition-api';
import useContentfulMedia from './contentful/useContentfulMedia';

export default function useVariantFeatures(productAttributes, appContext) {
  const { getImageByClass } = useContentfulMedia(appContext);
  const features = [
    { name: 'Skærm', icon: 'fa-thin fa-mobile-screen' },
    { name: 'Kamera', icon: 'fa-thin fa-camera' },
    { name: 'Oplader', customIcon: null },
    { name: 'WattInfo', customIcon: null },
    { name: 'EnergyEfficiency', customIcon: null }
  ];

  const specList = [
    { title: 'Højde', attrName: 'Height' },
    { title: 'Bredde', attrName: 'Width' },
    { title: 'Dybde', attrName: 'Depth' },
    { title: 'Vægt', attrName: 'Weight' },
    { title: 'Skærmstørrelse', attrName: 'ScreenSize' },
    { title: 'Skærmopløsning', attrName: 'ScreenResolution' },
    { title: 'Skærmtype', attrName: 'DisplayType' },
    { title: 'Kameraopløsning', attrName: 'CameraResolution' },
    { title: 'Frontkameraopløsning', attrName: 'FrontCameraResolution' },
    { title: 'Processor', attrName: 'Processor' },
    { title: 'RAM', attrName: 'RAM' },
    { title: 'Batterikapacitet', attrName: 'BatteryCapacity' },
    { title: 'Max ekstern hukommelse', attrName: 'MaxExternalMemory' },
    { title: 'Operativsystem', attrName: 'OperatingSystem' },
    { title: 'Tilslutning oplader', attrName: 'CableConnectivity' },
    { title: 'Tilslutning hovedtelefoner', attrName: 'HeadsetConnection' },
    { title: '5G', attrName: '5G' },
    { title: 'E-SIM', attrName: 'E-SIM' },
    { title: 'Dual SIM', attrName: 'DualSIM' }
  ];

  const getAttributeValue = (attributeName) => {
    return find({ name: attributeName })(productAttributes)?.value;
  };

  const getAttributeValueIfPresent = (attributeName) => {
    const value = getAttributeValue(attributeName);
    return value !== undefined && value !== null ? value : '';
  };

  // eslint-disable-next-line complexity
  const getFeatureIcon = async (name, chargerValue, energyClass) => {
    if (name === 'Oplader') {
      return getChargerIcon(chargerValue);
    }
    if (name === 'WattInfo') {
      return require('@/assets/images/chargers/charger-info.svg');
    }
    if (name === 'EnergyEfficiency') {
      // Try to get image from Contentful first
      if (energyClass) {
        // energyClass = 'A';
        const energyImage = await getImageByClass(energyClass);
        // console.log('energyImage: ' + JSON.stringify(energyImage));
        if (energyImage) {
          return energyImage.imageUrl;
        }
      }
      // Fallback to local image if Contentful image not found
      return 'Ukendt'; // require('@/assets/images/energy-efficiency-label.svg');
    }
    return null;
  };

  const getChargerIcon = (chargerValue) => {
    const normalizedValue = chargerValue === true;
    const chargerIcon = normalizedValue ? 'with-charger' : 'without-charger';

    return require(`@/assets/images/chargers/${chargerIcon}.svg`);
  };

  const generateWattInfo = (wattValue, usbPdValue) => {
    if (wattValue) {
      return ['Bruger ' + wattValue + ' W', usbPdValue ? 'USB-PD' : ''].filter(Boolean); // Add the required info
    } else {
      return ['', usbPdValue ? 'USB-PD' : ''].filter(Boolean); // Add the required info
    }
  };

  const generateOplader = (chargerValue) => {
    if (!chargerValue) {
      return ['Oplader medfølger ikke'];
    }
    return ['Oplader følger med'];
  };

  const generateEnergyEfficiency = (energyClass) => {
    if (energyClass) {
      return ['Produktblad'];
    }
    return [];
  };


  const generateFeatureValues = (chargerValue, wattValue, usbPdValue, energyClass) => {
    const wattInfo = generateWattInfo(wattValue, usbPdValue);
    const oplader = chargerValue !== null ? generateOplader(chargerValue) : [];
    const energyEfficiency = generateEnergyEfficiency(energyClass);

    const features = {};

    if (wattInfo.length) {
      features.WattInfo = wattInfo;
    }
    if (oplader.length) {
      features.Oplader = oplader;
    }
    if (energyEfficiency.length) {
      features.EnergyEfficiency = energyEfficiency;
    }
    return features;
  };

  const allowedFeatureNames = features.map(feature => feature.name);

  const filterFeatures = (featureValues) => {
    return Object.entries(featureValues)
      .filter(([name, values]) => allowedFeatureNames.includes(name) && values.length > 0);
  };

  const mapFeatures = (filteredFeatures, chargerValue, wattValue, usbPdValue, energyClass) => {
    const featurePromises = filteredFeatures.map(([name, values]) => createFeatureItem(name, values, chargerValue, wattValue, usbPdValue, energyClass));
    return Promise.all(featurePromises);
  };

  const createFeatureItem = async (name, values, chargerValue, wattValue, usbPdValue, energyClass) => {
    const feature = features.find((f) => f.name === name);
    if (!feature) {
      return null;
    }

    return {
      ...feature,
      values,
      customIcon: await getFeatureIcon(name, chargerValue, energyClass),
      extraFeatureInfo: getExtraFeatureInfo(name, wattValue, usbPdValue)
    };
  };

  const getExtraFeatureInfo = (name, wattValue, usbPdValue) => {
    if (name === 'WattInfo') {
      if (wattValue) {
        return [wattValue, 'W', usbPdValue ? 'USB-PD' : ''].filter(Boolean);
      } else {
        return ['', usbPdValue ? 'USB-PD' : ''].filter(Boolean);
      }
    }
    return [];
  };

  const featureList = async () => {
    const rawChargerValue = getAttributeValue('charger');
    const chargerValue = rawChargerValue === 'true' ? true : (rawChargerValue === 'false' ? false : null);
    const wattValue = getAttributeValueIfPresent('watt');
    const usbPdValue = getAttributeValueIfPresent('usb-pd') === 'true';
    const energyClass = getAttributeValueIfPresent('energy-efficiency-class');

    // console.log('energyClass: ' + energyClass)

    const featureValues = generateFeatureValues(chargerValue, wattValue, usbPdValue, energyClass);
    const filteredFeatures = filterFeatures(featureValues);
    return await mapFeatures(filteredFeatures, chargerValue, wattValue, usbPdValue, energyClass);
  };

  const productSpecList = computed(() => {
    return specList
      .map((spec) => {
        const value = getAttributeValue(spec.attrName);
        if (value !== undefined) {
          spec.description = value.toLowerCase() === 'true' ? 'Ja' : value;
          return spec;
        }
        return null;
      })
      .filter(Boolean);
  });

  return {
    productSpecList,
    featureList,
    getAttributeValue,
    getAttributeValueIfPresent
  };
}
